import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Clock } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { fetchArticle, fetchRelated, readTime, timeAgo, clock, imgUrl, articlePath, catName } from "@/lib/api";
import { BrandImg, NewsCard } from "@/components/news/NewsCard";
import { AdSlot } from "@/components/news/Ads";
import { ArticleActions } from "@/components/news/ArticleActions";
import { AISummary } from "@/components/news/AISummary";
import { AudioPlayer } from "@/components/news/AudioPlayer";
import { ShareSheet } from "@/components/news/ShareSheet";
import { InlineRecommended } from "@/components/news/InlineRecommended";
import { SectionTitle } from "@/components/news/CategorySection";
import { Skeleton } from "@/components/ui/skeleton";

function splitBlocks(html) {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  return [...doc.body.firstChild.childNodes].map((n) => n.outerHTML || n.textContent).filter((s) => s && s.trim());
}

function ArticleBody({ html, related, font }) {
  const blocks = useMemo(() => splitBlocks(html), [html]);
  const slots = { 2: related.slice(0, 2), 6: related.slice(2, 4) };
  const adAt = Math.min(4, Math.max(1, Math.floor(blocks.length / 2)));
  return (
    <div data-testid="article-body" className="article-body" style={{ fontSize: font }}>
      {blocks.map((b, i) => (
        <Fragment key={i}>
          {slots[i] && blocks.length > i + 1 && <InlineRecommended items={slots[i]} idx={i} />}
          {i === adAt && <AdSlot slot="article_inside" className="my-8" />}
          <div dangerouslySetInnerHTML={{ __html: b }} />
        </Fragment>
      ))}
    </div>
  );
}

function ArticleSEO({ a }) {
  const origin = window.location.origin;
  const url = origin + articlePath(a);
  const image = imgUrl(a.id, 1200);
  const desc = (a.excerpt || a.title).slice(0, 160);
  const ld = {
    "@context": "https://schema.org", "@type": "NewsArticle", headline: a.title.slice(0, 110), description: desc,
    image: [image], datePublished: a.date, dateModified: a.date, articleSection: catName(a.category),
    author: [{ "@type": "Organization", name: a.source }], mainEntityOfPage: { "@type": "WebPage", "@id": url },
    publisher: { "@type": "Organization", name: "Notícia 365", logo: { "@type": "ImageObject", url: `${origin}/icon.svg` } },
  };
  return (
    <Helmet>
      <title>{`${a.title} | Notícia 365`}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={a.title} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="article:published_time" content={a.date} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={image} />
      <script type="application/ld+json">{JSON.stringify(ld)}</script>
    </Helmet>
  );
}

export default function ArticlePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [related, setRelated] = useState([]);
  const [err, setErr] = useState(false);
  const [listen, setListen] = useState(false);
  const [summary, setSummary] = useState(false);
  const [share, setShare] = useState(false);
  const [font, setFont] = useState(() => +localStorage.getItem("n365-font") || 18);

  useEffect(() => {
    setA(null); setErr(false); setListen(false); setSummary(false); setRelated([]);
    fetchArticle(id).then(setA).catch(() => setErr(true));
    fetchRelated(id).then(setRelated).catch(() => {});
  }, [id]);
  useEffect(() => { localStorage.setItem("n365-font", font); }, [font]);

  if (err) return <div className="mx-auto max-w-3xl px-4 py-20"><h1 className="font-serif-display text-3xl font-black">Notícia não encontrada</h1><Link to="/" className="mt-4 inline-block text-primary underline">Voltar ao início</Link></div>;
  if (!a) return <div className="mx-auto max-w-3xl space-y-4 px-4 py-10"><Skeleton className="h-4 w-32" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-2/3" /><Skeleton className="aspect-video w-full rounded-2xl" /></div>;

  return (
    <article data-testid="article-page">
      <ArticleSEO a={a} />
      <div className="mx-auto max-w-3xl px-4 pt-6 sm:px-6">
        <button data-testid="article-back-btn" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))} className="mb-6 flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft size={16} />Voltar</button>
        <div className="flex flex-wrap items-center gap-2 font-mono-meta text-[11px] uppercase tracking-widest">
          <Link to={`/categoria/${a.category}`} data-testid="article-category-link" className="rounded bg-primary px-2 py-1 font-bold text-white">{catName(a.category)}</Link>
          <span className="rounded border px-2 py-1 text-muted-foreground">{a.source}</span>
        </div>
        <h1 data-testid="article-title" className="mt-5 font-serif-display text-3xl font-black leading-[1.1] sm:text-4xl lg:text-5xl">{a.title}</h1>
        {a.excerpt && <p className="mt-5 font-reading text-lg italic leading-relaxed text-muted-foreground line-clamp-3">{a.excerpt.replace(/\s*\[…\]|\s*\[\.\.\.\]/g, "")}</p>}
        <div className="mt-5 flex items-center gap-3 border-b pb-5 text-sm text-muted-foreground">
          <Clock size={14} className="text-primary" /><time dateTime={a.date} className="font-bold text-primary">{timeAgo(a.date)}</time><span>· {clock(a.date)}</span><span>·</span><span>{readTime(a.content)} min de leitura</span>
        </div>
        <div className="sticky top-[108px] z-30 -mx-4 bg-background/90 px-4 py-3 backdrop-blur-xl sm:mx-0 sm:px-0">
          <ArticleActions a={a} listening={listen} onListen={() => setListen(true)} summary={summary} onSummary={() => setSummary(true)} onShare={() => setShare(true)} font={font} setFont={setFont} />
        </div>
      </div>
      <figure className="mx-auto mt-4 max-w-5xl px-0 sm:px-6">
        <div className="aspect-video overflow-hidden bg-muted sm:rounded-2xl"><BrandImg a={a} w={1200} /></div>
      </figure>
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <AdSlot slot="article_top" className="mt-8" />
        {summary && <AISummary id={a.id} />}
        <div className="mt-8"><ArticleBody html={a.content} related={related} font={font} /></div>
        <AdSlot slot="article_bottom" className="my-8" />
        {!a.is_own && <a href={a.link} target="_blank" rel="noreferrer" data-testid="article-original-link" className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-foreground px-6 py-3 text-sm font-bold hover:bg-foreground hover:text-background">
          Ler no {a.source} <ExternalLink size={14} />
        </a>}
      </div>
      {related.length > 4 && (
        <section className="mx-auto mt-16 max-w-7xl border-t px-4 pt-10 sm:px-6 lg:px-8">
          <SectionTitle>Continue a ler</SectionTitle>
          <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">{related.slice(3, 6).map((r, i) => <NewsCard key={r.id} a={r} i={i} />)}</div>
        </section>
      )}
      {listen && <AudioPlayer a={a} onClose={() => setListen(false)} />}
      <ShareSheet a={a} open={share} onOpenChange={setShare} />
    </article>
  );
}
